import React, { useState, useEffect } from 'react';
import { ToolDeletionModal } from '../components/tools';
import { Wrench, Trash2, RefreshCw } from 'lucide-react';
import api from '../services/api';

interface Tool {
  id: string;
  name: string;
  description: string;
  input_schema?: any;
  server_id: string;
  server_name?: string;
  use_count?: number;
  last_used?: string;
}

export const ToolsManagement: React.FC = () => {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      setLoading(true);
      const response = await api.tools.list();
      setTools(response.tools);
      setError(null);
    } catch (err) {
      console.error('Error fetching tools:', err);
      setError('Error al cargar tools');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (tool: { id: string; name: string }) => {
    setSelectedTool(tool);
    setShowDeletionModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTool) return;

    try {
      await api.tools.delete(selectedTool.id);
      setShowDeletionModal(false);
      setSelectedTool(null);
      fetchTools(); // Refresh tools list
    } catch (error) {
      console.error('Failed to delete tool:', error);
      alert('Error al eliminar el tool');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestión de Tools</h1>
          <p className="text-gray-600 mt-1">
            Administra tools y sus dependencias
          </p>
        </div>
        <button
          onClick={fetchTools}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw size={20} />
          Actualizar
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Tools</p>
              <p className="text-2xl font-bold text-gray-900">{tools.length}</p>
            </div>
            <Wrench className="text-blue-500" size={32} />
          </div>
        </div>
      </div>

      {/* Tools Grid */}
      {tools.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Wrench className="mx-auto text-gray-400" size={48} />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No hay tools disponibles</h3>
          <p className="mt-2 text-gray-600">
            Los tools se agregan automáticamente desde los servidores MCP conectados
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tools.map((tool) => (
            <div
              key={tool.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench size={18} className="text-blue-600 flex-shrink-0" />
                    <h3 className="font-semibold text-gray-900 truncate">{tool.name || 'Sin nombre'}</h3>
                  </div>
                  {tool.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{tool.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    {tool.server_name && (
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        {tool.server_name}
                      </span>
                    )}
                    {tool.use_count !== undefined && (
                      <span>Usado {tool.use_count} {tool.use_count === 1 ? 'vez' : 'veces'}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteClick(tool)}
                  className="text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                  title="Eliminar tool"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tool Deletion Modal */}
      {selectedTool && (
        <ToolDeletionModal
          isOpen={showDeletionModal}
          onClose={() => {
            setShowDeletionModal(false);
            setSelectedTool(null);
          }}
          onConfirm={handleConfirmDelete}
          toolId={selectedTool.id}
          toolName={selectedTool.name}
        />
      )}
    </div>
  );
};
